import { SxProps, Theme } from '@mui/material/styles';
import { FC, ReactElement } from 'react';
import { Datagrid, DatagridClasses, DatagridProps, Identifier, RaRecord } from 'react-admin';

import { ExpandRowToggle } from './ExpandRowToggle';

const TOGGLE_CELL = 'expandable-datagrid-toggle-cell';

const EXPANDABLE_SX: SxProps<Theme> = {
  // react-admin's own expand button is aria-hidden and unfocusable; ExpandRowToggle replaces it, so
  // each row has exactly one expand control. Expand-all (ExpandAllButton, in this header) is not offered.
  [`& .${DatagridClasses.expandIconCell}, & .${DatagridClasses.expandHeader}`]: { display: 'none' },
  [`& .${TOGGLE_CELL}`]: { width: '1%', px: 0.5 },
};

/** One entry of MUI's sx array form. */
type SxEntry = Extract<SxProps<Theme>, ReadonlyArray<unknown>>[number];

// A plain Array.isArray narrows SxProps' ReadonlyArray member to any[], which would type the merged sx as any.
const isSxArray = (sx: SxProps<Theme>): sx is ReadonlyArray<SxEntry> => Array.isArray(sx);

/** One sx value as entries of MUI's array form, whatever shape it came in. */
const sxEntries = (sx: SxProps<Theme> | undefined): ReadonlyArray<SxEntry> =>
  sx == null ? [] : isSxArray(sx) ? sx : [sx];

export type ExpandableDatagridProps<RecordType extends RaRecord = RaRecord> = Omit<
  DatagridProps<RecordType>,
  'expand'
> & {
  /** An element or a component, never a bare ReactNode: `cond && <Panel />` would give every row a dead toggle. */
  expand: ReactElement | FC<{ id: Identifier; record: RecordType; resource: string }>;
  /** i18n key of the row toggle's accessible name. */
  toggleLabel: string;
};

/**
 * react-admin's Datagrid with a keyboard-reachable row expand: an ExpandRowToggle in the first
 * column replaces react-admin's aria-hidden expand cell. Use it for every Datagrid with `expand`.
 *
 * `expand`, `isRowExpandable` and `expandSingle` go to the Datagrid only. The toggle reads the
 * last two from the Datagrid's own context, where DatagridRow reads them, so the two cannot drift
 * apart; `expand` is required here, which is what the toggle relies on. The caller's `sx` is merged
 * after the wrapper's rules; every other prop, `children` included, passes through unchanged.
 */
export const ExpandableDatagrid = <RecordType extends RaRecord = RaRecord>({
  toggleLabel,
  sx,
  children,
  ...rest
}: ExpandableDatagridProps<RecordType>) => (
  <Datagrid {...rest} sx={[...sxEntries(EXPANDABLE_SX), ...sxEntries(sx)]}>
    <ExpandRowToggle
      label={false}
      sortable={false}
      cellClassName={TOGGLE_CELL}
      headerClassName={TOGGLE_CELL}
      labelKey={toggleLabel}
    />
    {children}
  </Datagrid>
);
